import type { PromoView } from '@snitch/contract'
import { cn } from '@snitch/ui/lib/cn'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleAlert,
  Info,
  LoaderCircle,
  TicketPercent,
} from 'lucide-react'
import { useState } from 'react'
import { ThemeToggle } from '../../../components/theme-toggle.tsx'
import { api, orpc } from '../../../lib/api.ts'
import { rememberInvitation } from '../../../lib/invitations.ts'

export const Route = createFileRoute('/_authed/_shell/profile/promos')({ component: PromoPage })
const date = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'Europe/Moscow',
})

function errorText(error: unknown) {
  if (error instanceof Error && 'code' in error && error.code === 'BAD_REQUEST')
    return 'Проверьте код: от 3 до 32 латинских букв, цифр или дефисов.'
  return error instanceof Error
    ? error.message
    : 'Не удалось проверить промокод. Попробуйте ещё раз.'
}

function PromoPage() {
  const { me } = Route.useRouteContext()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const invitation = useQuery({
    queryKey: ['referrals', 'mine', me.user.id],
    queryFn: () => api.referrals.mine(),
  })
  const key = ['promos', me.user.id]
  const saved = useQuery({
    queryKey: key,
    queryFn: () => api.promos.list(),
    staleTime: 0,
    refetchInterval: 60_000,
  })
  const [code, setCode] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const apply = useMutation({
    mutationFn: (code: string) => api.promos.apply({ code }),
    onSuccess: async (data, input) => {
      queryClient.setQueryData(key, data)
      if (data.invitationCode) {
        rememberInvitation({ code: data.invitationCode, via: 'code' })
        await navigate({
          to: '/i/$code',
          params: { code: data.invitationCode },
          search: { via: 'code' },
        })
        return
      }
      setNotice(`${input.trim().toUpperCase()} сохранён. Скидка появится в расчёте тарифа.`)
      setProblem(null)
      setCode('')
      await queryClient.invalidateQueries({ queryKey: orpc.billing.quote.key() })
    },
    onError: (error) => {
      setNotice(null)
      const message = errorText(error)
      setProblem(
        saved.data?.items.some((p) => p.status === 'active')
          ? `${message.replace(/[.!?…]+$/, '')}. Ваши действующие коды сохранены.`
          : message,
      )
    },
  })
  const remove = useMutation({
    mutationFn: (id: string) => api.promos.remove({ id }),
    onSuccess: async (data) => {
      queryClient.setQueryData(key, data)
      setNotice(null)
      setProblem(null)
      await queryClient.invalidateQueries({ queryKey: orpc.billing.quote.key() })
    },
    onError: (error) => setProblem(errorText(error)),
  })
  const busy = apply.isPending || remove.isPending
  const selected = saved.data?.items.find((p) => p.id === saved.data?.selectedId)
  const items = [...(saved.data?.items ?? [])].sort(
    (a, b) =>
      Number(b.id === selected?.id) - Number(a.id === selected?.id) ||
      b.percent - a.percent ||
      a.code.localeCompare(b.code),
  )
  return (
    <main className="flex flex-col gap-5 px-5 pt-[max(16px,var(--tg-viewport-content-safe-area-inset-top,0px))] pb-[max(28px,env(safe-area-inset-bottom))] md:gap-6 md:px-10 md:pt-7 md:pb-8 xl:px-14 xl:pt-8 xl:pb-10">
      <div className="flex h-11 items-center justify-between">
        <Link
          to="/profile"
          className="group flex min-h-11 items-center gap-2 rounded-full text-ink-2 text-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink"
        >
          <span className="flex size-11 items-center justify-center rounded-full bg-card text-ink transition-colors group-hover:bg-sunken">
            <ArrowLeft className="size-5" aria-hidden="true" />
          </span>
          Профиль
        </Link>
        <ThemeToggle className="hidden md:flex" />
      </div>
      <header className="flex flex-col gap-2">
        <h1 className="font-bold font-display text-[28px] leading-[1.25] tracking-[-1px] md:text-[32px]">
          Промокоды
        </h1>
        <p className="text-ink-2 text-sm leading-[1.4]">Сохраните скидку для следующего тарифа</p>
      </header>
      {notice ? (
        <p
          role="status"
          className="flex items-center gap-2.5 rounded-2xl bg-kb-green-soft px-4 py-3 text-kb-green-ink text-[13px] leading-[1.4]"
        >
          <Check className="size-[18px] shrink-0" aria-hidden="true" />
          {notice}
        </p>
      ) : null}
      {invitation.data ? (
        <div className="rounded-2xl bg-kb-green-soft p-4 text-kb-green-ink text-sm">
          <p className="font-semibold [overflow-wrap:anywhere]">
            Вас пригласил {invitation.data.inviterName}
          </p>
          <p className="mt-1">
            {invitation.data.used
              ? 'Скидка на первый тариф уже использована.'
              : `Скидка ${invitation.data.discountPercent}% на первый обычный тариф применится автоматически.`}
          </p>
          <Link
            to="/friends"
            className="mt-2 inline-flex min-h-11 items-center font-semibold underline underline-offset-4"
          >
            К друзьям
          </Link>
        </div>
      ) : null}
      <div className="grid items-start gap-6 xl:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-4">
          <form
            className="flex flex-col gap-4 rounded-3xl bg-card p-5 md:p-6"
            onSubmit={(e) => {
              e.preventDefault()
              setProblem(null)
              setNotice(null)
              apply.mutate(code)
            }}
          >
            <h2 className="font-semibold text-xl">Есть промокод?</h2>
            <div className="flex flex-col gap-2">
              <label htmlFor="promo-code" className="font-medium text-[13px]">
                Промокод
              </label>
              <input
                id="promo-code"
                name="promo"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Например, SNITCH20"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                required
                minLength={3}
                maxLength={32}
                aria-invalid={Boolean(problem)}
                aria-describedby={problem ? 'promo-error' : 'promo-help'}
                className={cn(
                  'h-12 min-w-0 rounded-[12px] border bg-bg px-3 text-base outline-none placeholder:text-ink-3 focus-visible:ring-2 focus-visible:ring-yellow',
                  problem ? 'border-red' : 'border-line',
                )}
              />
              {problem ? (
                <p
                  id="promo-error"
                  role="alert"
                  className="flex items-start gap-2 rounded-[12px] bg-red-soft px-3 py-2.5 text-kb-red-ink text-[13px] leading-[1.4]"
                >
                  <CircleAlert className="mt-px size-4 shrink-0" aria-hidden="true" />
                  {problem}
                </p>
              ) : null}
            </div>
            <button
              type="submit"
              disabled={busy || code.trim().length < 3}
              className="min-h-11 rounded-full bg-primary px-[18px] font-semibold text-on-primary text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:opacity-50"
            >
              {apply.isPending ? 'Проверяем…' : 'Применить'}
            </button>
            <p id="promo-help" className="text-ink-2 text-[13px] leading-[1.45]">
              Здесь можно ввести и код друга. Акционных кодов можно сохранить несколько — выберем
              самый выгодный из действующих.
            </p>
          </form>
          <p className="flex items-start gap-2.5 text-ink-2 text-[13px] leading-[1.5]">
            <Info className="mt-px size-[18px] shrink-0 text-ink-3" aria-hidden="true" />
            Каждый код — на одну покупку основного тарифа. На тестовый тариф, мобильный интернет и
            дополнительные места скидка не действует.
          </p>
        </div>
        <section className="flex min-w-0 flex-col gap-3.5" aria-label="Ваши промокоды">
          <h2 className="font-semibold text-xl">Ваши промокоды</h2>
          {saved.isPending ? (
            <p role="status" className="flex items-center gap-2 py-4 text-ink-2 text-sm">
              <LoaderCircle
                className="size-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              Загружаем промокоды…
            </p>
          ) : null}
          {saved.isError ? (
            <div role="alert" className="rounded-3xl bg-card p-5 text-sm">
              <p>Не удалось загрузить коды.</p>
              <button
                type="button"
                onClick={() => void saved.refetch()}
                className="mt-2 min-h-11 rounded-full bg-primary px-4 text-on-primary"
              >
                Повторить
              </button>
            </div>
          ) : null}
          {!saved.isPending && !saved.isError && items.length === 0 ? (
            <div className="flex flex-col gap-3 rounded-3xl bg-card px-5 py-6">
              <TicketPercent className="size-7 text-ink-3" aria-hidden="true" />
              <h3 className="font-semibold text-[17px]">Здесь будут ваши скидки</h3>
              <p className="text-ink-2 text-sm leading-[1.45]">
                Введите код — проверим его срок и условия. Покупку можно сделать позже.
              </p>
            </div>
          ) : null}
          {items.map((p) => (
            <PromoCard
              key={p.id}
              promo={p}
              selected={selected}
              busy={busy}
              onRemove={() => remove.mutate(p.id)}
            />
          ))}
          {selected ? (
            <>
              <Link
                to="/plans"
                className="flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary px-[18px] font-semibold text-on-primary text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                Выбрать тариф
                <ArrowRight className="size-[18px] text-on-primary-accent" aria-hidden="true" />
              </Link>
              <p className="text-ink-2 text-xs leading-[1.45]">
                Код считается использованным только после успешной покупки.
              </p>
            </>
          ) : null}
        </section>
      </div>
    </main>
  )
}

function PromoCard({
  promo,
  selected,
  busy,
  onRemove,
}: {
  promo: PromoView
  selected: PromoView | undefined
  busy: boolean
  onRemove: () => void
}) {
  const chosen = selected?.id === promo.id
  const labels = {
    active:
      selected && selected.percent > promo.percent
        ? `Сохранён · сейчас выгоднее ${selected.code}`
        : 'Сохранён · у выбранного кода такая же скидка',
    scheduled: 'Промокод ещё не действует',
    disabled: 'Отключён',
    expired: 'Срок действия истёк',
    exhausted: 'Лимит использований исчерпан',
  }
  return (
    <article
      className={cn('flex flex-col gap-3 rounded-3xl p-5', chosen ? 'bg-yellow-soft' : 'bg-card')}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="min-w-0 break-all font-semibold font-mono text-base">{promo.code}</h3>
        <p
          className={cn(
            'shrink-0 font-semibold text-2xl',
            chosen ? 'text-on-yellow-soft' : 'text-ink',
          )}
        >
          −{promo.percent}%
        </p>
      </div>
      <p
        className={cn(
          'flex items-start gap-1.5 text-[13px] leading-[1.4]',
          chosen ? 'font-semibold text-on-yellow-soft' : 'text-ink-2',
        )}
      >
        {chosen ? <Check className="mt-px size-4 shrink-0" aria-hidden="true" /> : null}
        {chosen ? 'Применится к следующему тарифу' : labels[promo.status]}
      </p>
      <div className="flex items-center justify-between gap-2">
        <p className="text-ink-2 text-xs">
          {promo.status === 'scheduled'
            ? `С ${date.format(promo.startsAt)}`
            : promo.expiresAt
              ? `До ${date.format(promo.expiresAt).replace(/\sг\.$/, '')}`
              : 'Без срока'}
        </p>
        <button
          type="button"
          disabled={busy}
          aria-label={`Убрать ${promo.code}`}
          onClick={onRemove}
          className="min-h-11 rounded-full px-[18px] font-semibold text-ink-2 text-sm hover:text-ink focus-visible:outline-2 focus-visible:outline-ink disabled:opacity-50"
        >
          Убрать
        </button>
      </div>
    </article>
  )
}
